import { useEffect, useMemo, useState } from "react";
import { l37OfferFromPriceRow, priceOriginLabel, type L37PriceOffer } from "../domain/priceOrigin.ts";
import {
  isPilotSourceCode,
  priceResolutionSnapshot,
  resolvePilotConcreteArticle,
  type PilotPriceResolution,
  type ReferencePriceLine,
} from "../domain/pilotConcreteResolution.ts";
import { supabase } from "../lib/supabase";

const money = (value: number | null) => value === null
  ? "—"
  : Number(value).toLocaleString("pt-PT", { style: "currency", currency: "EUR" });

const qty = (value: number | null, unit: string) => value === null
  ? "Por definir"
  : Number(value).toLocaleString("pt-PT", { maximumFractionDigits: 3 }) + " " + unit;

function originClass(origin: string) {
  if (origin === "l37_validated") return "origin-validated";
  if (origin === "l37_provisional") return "origin-provisional";
  if (origin === "cype_fallback") return "origin-cype";
  return "origin-missing";
}

export default function PilotPricePanel({
  sourceCode,
  referenceLines,
  parameters,
  quantityOverrides,
  cypeOriginalUnitPrice,
  onResolved,
}: {
  sourceCode: string | null | undefined;
  referenceLines: ReferencePriceLine[];
  parameters: Record<string, string>;
  quantityOverrides?: Record<string, number | null>;
  cypeOriginalUnitPrice?: number | null;
  onResolved?: (resolution: PilotPriceResolution | null) => void;
}) {
  const [offers, setOffers] = useState<L37PriceOffer[]>([]);
  const [offersError, setOffersError] = useState("");
  const [ready, setReady] = useState(false);
  const active = isPilotSourceCode(sourceCode);

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    setReady(false);
    void supabase
      .from("resource_prices")
      .select("id,unit_price,status,is_reference,price_origin,metadata,resources!inner(code,unit)")
      .or("and(is_reference.eq.true,status.eq.approved),and(price_origin.eq.l37_provisional,status.eq.candidate,is_reference.eq.false)")
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          setOffers([]);
          setOffersError(error.message);
        } else {
          setOffers((data ?? []).map((row) => l37OfferFromPriceRow(row)).filter((row): row is L37PriceOffer => row !== null));
          setOffersError("");
        }
        setReady(true);
      });
    return () => { cancelled = true; };
  }, [active]);

  const resolution = useMemo(() => {
    if (!active) return null;
    return resolvePilotConcreteArticle({
      sourceCode,
      referenceLines,
      parameters,
      offers,
      quantityOverrides,
      cypeOriginalUnitPrice,
    });
  }, [active, sourceCode, referenceLines, parameters, offers, quantityOverrides, cypeOriginalUnitPrice]);

  const fingerprint = resolution ? JSON.stringify(priceResolutionSnapshot(resolution)) : "";
  useEffect(() => {
    if (!active) {
      onResolved?.(null);
      return;
    }
    if (!ready) return;
    onResolved?.(resolution);
  }, [fingerprint, active, ready]);

  if (!active || !resolution) return null;

  return <section className="card library-section" aria-label="Origem do preço e cobertura">
    <p className="eyebrow">Hierarquia de preço · {resolution.sourceCode}</p>
    <h3>PU seco por origem</h3>
    <p className="muted">A ordem é fixa: L37 validado, depois L37 provisório, depois fallback CYPE da linha desta ficha quando é tecnicamente compatível, senão sem preço. Um fallback CYPE conserva o código, a unidade e o preço de origem e não é um preço L37.</p>
    <p className="muted">Só entram preços L37 já gravados como referência validada ou como provisório aceite. O pacote de validação do piloto não é escrito na tabela de preços.</p>
    {!ready ? <p role="status">A carregar preços L37…</p> : null}
    {offersError ? <div className="alert alert-error">Não foi possível ler os preços L37: {offersError}. As linhas sem preço ficam de fora do PU.</div> : null}
    <div className="coverage-grid">
      {resolution.coverage.map((bucket) => <article className={"coverage-bucket " + originClass(bucket.origin)} key={bucket.origin}>
        <span>{bucket.label}</span>
        <strong>{money(bucket.economicValue)}</strong>
        <small>{bucket.sharePct === null ? "Sem PU incluído" : bucket.sharePct.toLocaleString("pt-PT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " % do PU seco"}</small>
      </article>)}
    </div>
    <div className="library-table-wrap"><table className="library-table">
      <thead><tr><th>Recurso</th><th>Consumo</th><th>Origem</th><th>Preço</th><th>Parcial</th></tr></thead>
      <tbody>{resolution.lines.map((line) => <tr key={line.key}>
        <td><strong>{line.label}</strong><small className="mono">{line.resourceCode}</small><small>{line.note}</small></td>
        <td>{qty(line.quantity, line.unit)}</td>
        <td><span className={"origin-pill " + originClass(line.priceOrigin)}>{priceOriginLabel(line.priceOrigin)}</span>
          {line.priceOrigin === "cype_fallback" ? <small>CYPE {line.sourceCode} · {line.sourceUnit} · {money(line.sourceUnitPrice)}</small> : null}
        </td>
        <td>{line.priceOrigin === "missing" ? <span className="price-missing">Sem preço</span> : money(line.unitPrice)}</td>
        <td>{line.includedInDryPu ? money(line.partial) : "—"}</td>
      </tr>)}</tbody>
      <tfoot><tr>
        <th colSpan={4}>PU seco incluído / m³</th>
        <td>{money(resolution.dryUnitPrice)}</td>
      </tr></tfoot>
    </table></div>
    {resolution.excluded.map((line) => <p key={line.key}><strong>Excluído do PU seco:</strong> {line.label} · {line.quantity ?? "—"} {line.unit}
      {line.cypePartial !== null ? " · parcial CYPE " + money(line.cypePartial) : ""}. {line.note}</p>)}
    {resolution.missingResourceCodes.length ? <p className="technical-invalid">Sem preço, fora da soma: {resolution.missingResourceCodes.join(", ")}. Um recurso sem preço não entra como zero.</p> : null}
    <p className="muted">PU CYPE de origem: <strong>{money(resolution.cypeOriginalUnitPrice)}</strong>. Este valor é a referência da ficha, não um preço L37.</p>
  </section>;
}
